// Ein Gerät der Geräte-Seite aus dem Homelab-Protokoll (Topic `geraet.stand`,
// docs/protokoll.md › „Geräte über das Protokoll“) in die Form, die die Seite
// aus der REST-Antwort kennt (`Device` aus iot-devices.svelte.ts).
//
// Rein, ohne Runen und ohne Laufzeit-Importe, damit node (Test
// lib/iot-devices-protokoll.test.mjs) sie laden kann. Das Topic hat keine
// skalierten Felder; `skaliere` ist trotzdem der Weg des Stores (er bleibt
// richtig, wenn das Schema eines bekommt).

import type { Device, DeviceEntity } from "./iot-devices.svelte";
import type { GeraetEntitaet, GeraetStand } from "./protokoll/schema.gen";

/** „geraet.<slug>.stand“ → <slug>; alles andere → undefined. */
export function slugAusInstanz(instanz: string): string | undefined {
  if (!instanz.startsWith("geraet.") || !instanz.endsWith(".stand")) return undefined;
  const slug = instanz.slice("geraet.".length, -".stand".length);
  return slug === "" ? undefined : slug;
}

/** Unix-Sekunden → ISO-Text ohne Millisekunden (`2026-11-02T12:33:07Z`, wie
 *  die REST-Antwort ihn trägt); fehlt/null → undefined. */
export function isoAusSek(s: unknown): string | undefined {
  return typeof s === "number" && Number.isFinite(s) ? new Date(s * 1000).toISOString().replace(".000Z", "Z") : undefined;
}

function entitaet(e: GeraetEntitaet): DeviceEntity {
  return {
    slug: e.slug ?? "",
    name: e.name ?? "",
    kind: e.art ?? "",
    unit: e.einheit ?? "",
    state: e.zustand ?? "",
    deviceClass: e.klasse ?? "",
    haEntityId: e.ext_id ?? "",
    updatedAt: isoAusSek(e.aktualisiert) ?? "",
  };
}

/** Das Gerät mit dem Slug `slug` aus seinem Stand. Fehlende Texte werden "",
 *  `online` fehlt → null, `entities` ist immer ein Array; die optionalen Felder
 *  (lqi, funkmelder, disableDomain, disableEntity, versorgt, ausGesperrt)
 *  fehlen am Ergebnis, wenn sie im Stand fehlen -- wie in der REST-Antwort. */
export function geraetAusProtokoll(slug: string, s: GeraetStand): Device {
  const d: Device = {
    slug,
    name: s.name ?? "",
    label: s.label ?? "",
    area: s.bereich ?? "",
    kind: s.art ?? "",
    manufacturer: s.hersteller ?? "",
    model: s.modell ?? "",
    integration: s.integration ?? "",
    integrationSlug: s.integration_slug ?? "",
    haDeviceId: s.adresse ?? "",
    online: typeof s.online === "boolean" ? s.online : null,
    lastSeen: isoAusSek(s.zuletzt) ?? "",
    entities: (s.entitaeten ?? []).map(entitaet),
  };
  if (typeof s.lqi === "number") d.lqi = s.lqi;
  if (s.stilllegung_domain) d.disableDomain = s.stilllegung_domain;
  if (s.stilllegung_id) d.disableEntity = s.stilllegung_id;
  if (s.versorgt) d.versorgt = s.versorgt;
  if (s.aus_gesperrt) d.ausGesperrt = true;
  if (s.funkmelder) {
    d.funkmelder = { id: s.funkmelder.id ?? "", aus: s.funkmelder.aus ?? false, abschaltbar: s.funkmelder.abschaltbar ?? false };
  }
  return d;
}

/** Felder, die der Server weglässt, wenn es nichts zu sagen gibt (`omitempty`)
 *  und die Protokoll-Stand ebenso weglässt. */
const OPTIONAL = ["lqi", "funkmelder", "disableDomain", "disableEntity", "versorgt", "ausGesperrt"] as const;

/** Übernimmt `neu` IN-PLACE in `ziel` (Objekt-Identität bleibt, damit nichts
 *  zappelt): alle Felder kopieren, und die optionalen, die `neu` nicht trägt,
 *  am Ziel entfernen -- Object.assign allein nähme sie nicht zurück. */
export function uebernimm(ziel: Device, neu: Device): void {
  Object.assign(ziel, neu);
  for (const k of OPTIONAL) if (neu[k] == null) delete ziel[k];
}
